import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useIdleTimer from '../../hooks/useIdleTimer';
import { tokenManager } from '../../services/utils/token.manager';
import { showSessionExpiryDialog } from './SessionExpiryDialog';
import toast from 'react-hot-toast';
import axios from 'axios';
import { API_CONFIG } from '../../services/config/api.config';

// 30 minutes in milliseconds
const IDLE_TIMEOUT = 30 * 60 * 1000;

// For testing: 30 seconds (uncomment this line and comment the above for testing)
// const IDLE_TIMEOUT = 30 * 1000;

/**
 * Component that handles idle session detection and shows dialog
 * Place this component inside your authenticated layout
 */
const IdleSessionHandler = () => {
    const navigate = useNavigate();
    const [isDialogShown, setIsDialogShown] = useState(false);

    const handleIdle = useCallback(async () => {
        // Don't show dialog if already shown or user is not logged in
        if (isDialogShown || !tokenManager.getToken()) {
            return;
        }

        setIsDialogShown(true);

        try {
            // Show the session expiry dialog
            const shouldContinue = await showSessionExpiryDialog();

            if (shouldContinue) {
                // User clicked "Yes, Continue" - refresh the token
                const refreshToken = tokenManager.getRefreshToken();

                if (refreshToken) {
                    try {
                        const response = await axios.post(`${API_CONFIG.BASE_URL}/v1/auth/refresh`, {
                            refresh_token: refreshToken,
                        });

                        const tokenData = response.data.data?.[0];
                        const newAccessToken = tokenData?.access_token;
                        const newRefreshToken = tokenData?.refresh_token;

                        if (newAccessToken) {
                            tokenManager.setToken(newAccessToken);
                            if (newRefreshToken) {
                                tokenManager.setRefreshToken(newRefreshToken);
                            }
                            toast.success('Session extended successfully');
                        } else {
                            throw new Error('No access token received');
                        }
                    } catch (refreshError) {
                        // Refresh failed, logout user
                        tokenManager.clearTokens();
                        toast.error('Session expired. Please login again.');
                        navigate('/login');
                    }
                } else {
                    // No refresh token, logout
                    tokenManager.clearTokens();
                    toast.error('Session expired. Please login again.');
                    navigate('/login');
                }
            } else {
                // User clicked "No, Logout"
                tokenManager.clearTokens();
                toast.success('You have been logged out.');
                navigate('/login');
            }
        } finally {
            setIsDialogShown(false);
        }
    }, [isDialogShown, navigate]);

    // Check if user is authenticated before enabling idle timer
    const isAuthenticated = !!tokenManager.getToken();

    // Use the idle timer hook
    useIdleTimer(IDLE_TIMEOUT, handleIdle, isAuthenticated);

    // This component doesn't render anything visible
    return null;
};

export default IdleSessionHandler;
